import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X, Send, Loader2, Maximize2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useMessages } from "@/contexts/MessagesContext";
import ChatHeaderPill from "@/components/ChatHeaderPill";

function timeShort(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return d.toLocaleDateString();
  } catch { return ""; }
}

/**
 * Floating chat drawer (bottom-right). Mounted once at app level via MessagesProvider.
 * Visible only when drawerOpen && openPeer && user is logged in.
 */
export default function MessagesDrawer() {
  const { user } = useAuth();
  const { openPeer, drawerOpen, closeDrawer, refreshUnread } = useMessages();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [peerInfo, setPeerInfo] = useState({ online: false, typing: false, lastSeen: null });
  const scrollRef = useRef(null);
  const pollRef = useRef(null);
  const lastTypingRef = useRef(0);

  const load = async () => {
    if (!openPeer) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/dm/${openPeer.user_id}`, { params: { limit: 60 } });
      setMessages(Array.isArray(data?.messages) ? data.messages : []);
      if (data?.peer) {
        setPeerInfo({
          online: !!data.peer.online,
          typing: !!data.peer.typing,
          lastSeen: data.peer.last_seen || null,
        });
      }
      // Mark thread read
      try {
        await api.post(`/dm/${openPeer.user_id}/read`);
        refreshUnread();
      } catch { /* ignore */ }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load messages");
    } finally { setLoading(false); }
  };

  // Initial load + polling while drawer open (poll faster when waiting on peer typing)
  useEffect(() => {
    if (!drawerOpen || !openPeer) return;
    load();
    pollRef.current = setInterval(load, 4000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen, openPeer?.user_id]);

  // Auto scroll to bottom on new messages
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault?.();
    if (!text.trim() || !openPeer) return;
    setSending(true);
    try {
      const { data } = await api.post(`/dm/${openPeer.user_id}`, { text: text.trim() });
      setMessages((prev) => [...prev, data]);
      setText("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Send failed");
    } finally { setSending(false); }
  };

  if (!user || !drawerOpen || !openPeer) return null;

  const sendTypingPing = () => {
    const now = Date.now();
    if (now - lastTypingRef.current < 3000) return;
    lastTypingRef.current = now;
    api.post(`/dm/${openPeer.user_id}/typing`).catch(() => {});
  };

  return (
    <div
      data-testid="messages-drawer"
      className="fixed z-[55] bottom-4 right-4 w-[360px] max-w-[calc(100vw-32px)] max-h-[70vh] flex flex-col bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl shadow-2xl overflow-hidden animate-fade-up"
    >
      <ChatHeaderPill
        peer={openPeer}
        online={peerInfo.online}
        typing={peerInfo.typing}
        lastSeen={peerInfo.lastSeen}
      >
        <Link
          to="/messages"
          onClick={closeDrawer}
          data-testid="drawer-expand"
          title="Open full Messages"
          className="p-1.5 rounded-full hover:bg-white/15 text-white"
        >
          <Maximize2 className="w-3.5 h-3.5" />
        </Link>
        <button
          onClick={closeDrawer}
          data-testid="drawer-close"
          title="Close"
          className="p-1.5 rounded-full hover:bg-white/15 text-white"
        >
          <X className="w-4 h-4" />
        </button>
      </ChatHeaderPill>

      <div
        ref={scrollRef}
        data-testid="drawer-messages"
        className="flex-1 overflow-y-auto px-3 py-3 space-y-2"
      >
        {loading && messages.length === 0 ? (
          <div className="flex items-center justify-center gap-2 text-xs text-[var(--tt-text-muted)] py-6">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading...
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-xs text-[var(--tt-text-muted)] py-8">
            No messages yet. Say hi to <span className="text-[var(--tt-primary)] font-semibold">@{openPeer.name}</span>.
          </div>
        ) : (
          messages.map((m) => {
            const mine = m.from_user_id === user.user_id;
            return (
              <div
                key={m.message_id}
                data-testid={`dm-${m.message_id}`}
                className={`flex ${mine ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] px-3 py-1.5 rounded-2xl text-sm break-words
                    ${mine
                      ? "bg-[var(--tt-primary)] text-white rounded-br-sm"
                      : "bg-[var(--tt-surface-2)] text-zinc-100 rounded-bl-sm"}`}
                >
                  <div className="whitespace-pre-wrap">{m.text}</div>
                  <div className={`text-[9px] uppercase tracking-wider mt-1 ${mine ? "text-white/70" : "text-[var(--tt-text-faint)]"}`}>
                    {timeShort(m.created_at)}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <form onSubmit={send} className="border-t border-[var(--tt-border)] px-2.5 py-2 flex items-end gap-2">
        <textarea
          value={text}
          onChange={(e) => { setText(e.target.value); if (e.target.value.trim()) sendTypingPing(); }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
          }}
          placeholder={`Message @${openPeer.name}...`}
          rows={1}
          maxLength={4000}
          data-testid="drawer-input"
          className="flex-1 bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-2xl px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)] resize-none max-h-32"
        />
        <button
          type="submit"
          disabled={sending || !text.trim()}
          data-testid="drawer-send"
          className="bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white w-9 h-9 flex items-center justify-center rounded-full disabled:opacity-50"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
    </div>
  );
}
